import { STATUS_CODE } from '@vuejs/helpers/_constants'

import type { ApiClient } from '../api'
import { getLoginMethod } from '../apiRoutes'

export interface LoginMethodResponse {
	auth_method: 'email_required' | 'keycloak' | 'password'
}

export interface SignInResponse {
	success?: boolean
	error?: string
	redirect_url?: string
}

/**
 * Determines the authentication method for a given email address or phone number.
 * Checks whether the user should authenticate via Keycloak SSO or password. When an
 * SSO-eligible admin enters a phone number, returns 'email_required' so the UI can
 * prompt them to sign in with their email instead (Keycloak does not support phone login).
 *
 * @param api - API client instance
 * @param email - User email or phone number
 * @returns Promise resolving to authentication method ('email_required', 'keycloak', or 'password')
 * @throws Error if the API request fails
 */
export const getLoginMethodForEmail = async (
	api: ApiClient,
	email: string
): Promise<LoginMethodResponse> => {
	const response = await api.get<LoginMethodResponse>(getLoginMethod, { email })

	return response.data
}

/**
 * Authenticates a user with email/phone and password.
 * Makes a POST request to the sign_in endpoint with credentials.
 *
 * @param login - Email address or phone number
 * @param password - User password
 * @param authenticityToken - CSRF token for the request
 * @returns Promise resolving to sign-in response with redirect URL on success
 * @throws Error with descriptive message on authentication failure or network error
 */
export const signInWithPassword = async (
	login: string,
	password: string,
	authenticityToken: string
): Promise<SignInResponse> => {
	const response = await fetch('/users/sign_in', {
		body: JSON.stringify({
			user: {
				login,
				password,
			},
		}),
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json',
			'X-CSRF-Token': authenticityToken,
		},
		method: 'POST',
	})

	const contentType = response.headers.get('content-type')

	if (!contentType || !contentType.includes('application/json')) {
		if (!response.ok) {
			throw new Error('Server error. Please try again later.')
		}
		throw new Error('Invalid email or password')
	}

	const data = await response.json() as SignInResponse

	if (!response.ok) {
		if (response.status === STATUS_CODE.UNAUTHORIZED) {
			throw new Error('Invalid email or password')
		}
		throw new Error(data.error || 'Authentication failed')
	}

	return data
}
